import _ from "lodash";
import { computed } from "mobx";
import { useMemo } from "react";
import { useLeadStore, usePipedriveStore } from "..";
import { GeoCoordinate } from "../interfaces/GeoCoordinate";
import INotification, { NotificationType } from "../interfaces/INotification";
import useCurrentOrFallbackPipedriveUserId from "./useCurrentOrFallbackPipedriveUserId";

const useNotifications = (searchFilter: string, sortBy: string): INotification[] => {
    const leadStore = useLeadStore();
    const pipedriveStore = usePipedriveStore();
    const pipedriveUserId = useCurrentOrFallbackPipedriveUserId();
    const currentUserActivities = useMemo(() => computed(() => pipedriveStore.activities.filter(a => a.userId === pipedriveUserId)), [pipedriveUserId]).get();

    const leadNotifications: INotification[] = useMemo(() => computed(() => {
        return leadStore.leads
            .filter(l => l.activityId)
            .map(l => ({
                lead: l,
                activity: currentUserActivities.find(a => a.id === l.activityId),
            }))
            .filter(n => n.activity)
            .map(n => ({
                type: NotificationType.Lead,
                leadId: n.lead.id,
                name: n.lead.address || "",
                dueDate: n.activity!.dueDate,
                subject: n.activity!.subject,
                centerPoint: n.lead.centerPoint,
                createdDate: n.activity!.createdDate,
                modifiedDate: n.activity!.modifiedDate
            }));
    }), [currentUserActivities]).get();

    const dealNotifications: INotification[] = useMemo(() => computed(() => {
        return pipedriveStore.deals
            .filter(d => !d.dealShapes.length || !d.lat || !d.lon)
            .map(d => ({
                type: NotificationType.Deal,
                dealId: d.id,
                name: d.name,
                address: d.address || "",
                centerPoint: new GeoCoordinate(d.lat, d.lon),
                createdDate: d.createdAt,
                modifiedDate: d.updatedAt
            }));
    }), []).get();

    const sortedNotifications = useMemo(() => {
        const allNotifications = [...leadNotifications, ...dealNotifications];

        switch (sortBy) {
            case NotificationsSortingType.ModifiedNewest:
                return _.orderBy(allNotifications, (n) => n.modifiedDate, "desc");

            case NotificationsSortingType.ModifiedOldest:
                return _.orderBy(allNotifications, (n) => n.modifiedDate, "asc");

            case NotificationsSortingType.CreatedNewest:
                return _.orderBy(allNotifications, (n) => n.createdDate, "desc");

            case NotificationsSortingType.CreatedOldest:
                return _.orderBy(allNotifications, (n) => n.createdDate, "asc");

            case NotificationsSortingType.DueDateNewest:
                return _.orderBy(allNotifications, (n) => (n.dueDate ?? -Infinity), "desc");

            case NotificationsSortingType.DueDateOldest:
                return _.orderBy(allNotifications, (n) => (n.dueDate ?? Infinity), "asc");

            default:
                return _.orderBy(allNotifications, (n) => (n.dueDate ?? -Infinity), "desc");
        }
    }, [leadNotifications, dealNotifications, sortBy]);

    return useMemo(() => {
        if (!searchFilter)
            return sortedNotifications;

        const searchFilterInLowerCase = searchFilter.toLowerCase();

        return sortedNotifications.filter(n =>
            n.name.toLowerCase().includes(searchFilterInLowerCase) ||
            n.address && n.address.toLowerCase().includes(searchFilterInLowerCase) ||
            n.subject && n.subject.toLowerCase().includes(searchFilterInLowerCase)
        );
    }, [sortedNotifications, searchFilter]);
}

export default useNotifications;

export class NotificationsSortingType {
    static ModifiedNewest = "ModifiedNewest";
    static ModifiedOldest = "ModifiedOldest";
    static CreatedNewest = "CreatedNewest";
    static CreatedOldest = "CreatedOldest";
    static DueDateNewest = "DueDateNewest";
    static DueDateOldest = "DueDateOldest";
}